/**
 * Comment scoring tab for the Ranking Evaluation structure.
 *
 * Lets evaluators view fetched comments alongside existing scores, edit scores
 * inline, and persist them back to the server. Shares conversation-navigation
 * controls with the Side-by-Side tab.
 */

import { Edit as EditIcon } from '@carbon/icons-react';
import type { Comment as WireComment } from '@repo/shared/orpc/procedures/comments';
import { useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, Pad, Separator, VertBox } from 'component/basics';
import { ActionButton, CTAButton } from 'component/button';
import { Comment } from 'component/comment';
import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { Heading, TextField, UtilityText } from 'component/text';
import type { EvalCommentRating } from 'data/ranking-eval';
import {
    submitEvalCommentRatingAsync,
    useEvalCommentRatings,
    useEvalConversationComments,
    useImportableEvalConversations,
    useSelectedEvalConversationSet,
} from 'data/ranking-eval';
import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useDatastore, useInstanceKey, usePersonaKey } from 'util/datastore';
import { t } from 'util/stdtypes';

import { SideBySideNavigationBar, useImportConversation } from './side-by-side';

// ============================================================================
// COMMENT SCORING
// ============================================================================

const RATING_DESCRIPTIONS: Record<number, string> = {
    1: "Very bad. Maybe shouldn't be shown at all.",
    2: 'Poor quality. Adds little value. Should rank very low.',
    3: "Okay. Contributes something, doesn't stand out. Most common.",
    4: 'Good. Unique and valuable. Should rank near top.',
    5: 'Best. As good as it gets. An exemplar.',
};

function starsString(count: number): string {
    return '\u2605'.repeat(count);
}

// Interactive 1-5 star selector
function StarRating({ value, onChange }: { value: number; onChange: (rating: number) => void }) {
    const s = StarRatingStyle;
    return (
        <View style={s.row}>
            {[1, 2, 3, 4, 5].map((star) => (
                <Pressable key={star} onPress={() => onChange(star)} style={s.star}>
                    <UtilityText style={s.starText} text={star <= value ? '\u2605' : '\u2606'} />
                </Pressable>
            ))}
        </View>
    );
}
const StarRatingStyle = StyleSheet.create({
    row: {
        flexDirection: 'row',
        gap: 4,
    },
    star: {
        padding: 4,
    },
    starText: {
        fontSize: 24,
        lineHeight: 28,
    },
});

// Star rating legend shown at the top of the scoring screen
function StarRatingLegend() {
    const s = useStarRatingLegendStyle();
    return (
        <View style={s.legend}>
            <UtilityText label={t('ranking-eval.scoring_legend_title', 'Rating Guide')} type='small' weight='medium' />
            {[1, 2, 3, 4, 5].map((star) => (
                <UtilityText key={star} text={`${starsString(star)} = ${RATING_DESCRIPTIONS[star]}`} type='small' />
            ))}
        </View>
    );
}
const useStarRatingLegendStyle = createThemedStyles((tokens) => ({
    legend: {
        gap: 4,
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: tokens.colors.colorSurfaceSecondary,
        borderRadius: 8,
    },
}));

// All ratings summary shown after the current user has rated
function AllCommentRatings({
    conversationRatings,
    commentKey,
    personaKey,
    onEdit,
}: {
    conversationRatings: Record<string, EvalCommentRating> | null | undefined;
    commentKey: string;
    personaKey: string | null;
    onEdit: () => void;
}) {
    const { colors } = useTheme();
    const allRatings = Object.values(conversationRatings || {}).filter((r) => r.commentKey === commentKey);

    return (
        <VertBox gap={4}>
            {allRatings.map((r) => (
                <HorizBox center gap={4} key={r.key}>
                    <UtilityText
                        text={`${r.fromName || r.from}: ${starsString(r.rating)}${r.reason ? ` — ${r.reason}` : ''}`}
                        type='small'
                    />
                    {r.from === personaKey && <ActionButton icon={EditIcon} onPress={onEdit} />}
                </HorizBox>
            ))}
            {allRatings.length === 0 && (
                <UtilityText color={colors.colorTextSecondary} label={t('ranking-eval.no_ratings_yet', 'No ratings yet.')} type='small' />
            )}
        </VertBox>
    );
}

// Right-side panel for rating a single comment
function CommentRatingPanel({
    commentKey,
    conversationRatings,
    onRatingSubmitted,
    personaKey,
}: {
    commentKey: string;
    conversationRatings: Record<string, EvalCommentRating> | null | undefined;
    onRatingSubmitted: () => void;
    personaKey: string | null;
}) {
    const s = CommentRatingPanelStyle;
    const datastore = useDatastore();
    const { colors } = useTheme();
    const myRecordKey = `${personaKey}-${commentKey}`;
    const existingRating = conversationRatings?.[myRecordKey];
    const [editing, setEditing] = useState(false);
    const [rating, setRating] = useState(existingRating?.rating || 0);
    const [reason, setReason] = useState(existingRating?.reason || '');
    const [submitting, setSubmitting] = useState(false);
    const hasRated = !!existingRating;

    async function handleSubmit() {
        if (rating < 1 || rating > 5 || submitting) return;
        setSubmitting(true);
        try {
            await submitEvalCommentRatingAsync(datastore, { commentKey, rating, reason: reason || undefined });
            setEditing(false);
            onRatingSubmitted();
        } finally {
            setSubmitting(false);
        }
    }

    if (hasRated && !editing) {
        return (
            <View style={s.panel}>
                <AllCommentRatings
                    commentKey={commentKey}
                    conversationRatings={conversationRatings}
                    onEdit={() => {
                        setRating(existingRating.rating);
                        setReason(existingRating.reason || '');
                        setEditing(true);
                    }}
                    personaKey={personaKey}
                />
            </View>
        );
    }

    return (
        <View style={s.panel}>
            <StarRating onChange={setRating} value={rating} />
            {rating > 0 && <UtilityText color={colors.colorTextSecondary} text={RATING_DESCRIPTIONS[rating]} type='small' />}
            <TextField
                multiline
                onChange={setReason}
                placeholderLabel={t('ranking-eval.rating_reason_placeholder', 'Why this rating?')}
                value={reason}
            />
            <HorizBox gap={8}>
                <CTAButton
                    disabled={rating < 1 || submitting}
                    label={hasRated ? t('ranking-eval.update_rating', 'Update Rating') : t('ranking-eval.submit_rating', 'Submit Rating')}
                    onPress={handleSubmit}
                    type='primary'
                />
                {editing && (
                    <CTAButton label={t('ranking-eval.cancel_edit', 'Cancel')} onPress={() => setEditing(false)} type='secondary' />
                )}
            </HorizBox>
        </View>
    );
}
const CommentRatingPanelStyle = StyleSheet.create({
    panel: {
        width: 300,
        flexShrink: 0,
        gap: 12,
    },
});

// A comment with its rating panel side by side
function ScoredComment({
    comment,
    conversationRatings,
    personaKey,
    onRatingSubmitted,
}: {
    comment: WireComment;
    conversationRatings: Record<string, EvalCommentRating> | null | undefined;
    personaKey: string | null;
    onRatingSubmitted: () => void;
}) {
    const s = ScoredCommentStyle;
    return (
        <View style={s.row}>
            <View style={s.commentSide}>
                <Comment commentTree={{ comment, replies: [], boosted: false }} />
            </View>
            <CommentRatingPanel
                commentKey={comment.commentKey}
                conversationRatings={conversationRatings}
                onRatingSubmitted={onRatingSubmitted}
                personaKey={personaKey}
            />
        </View>
    );
}
const ScoredCommentStyle = StyleSheet.create({
    row: {
        flexDirection: 'row',
        gap: 20,
        alignItems: 'flex-start',
    },
    commentSide: {
        width: 560,
        flexShrink: 0,
    },
});

// Stable shuffle using a seed string
function seededShuffle<T>(array: T[], seed: string): T[] {
    const result = [...array];
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash << 5) - hash + seed.charCodeAt(i);
        hash |= 0;
    }
    for (let i = result.length - 1; i > 0; i--) {
        hash = (hash * 1103515245 + 12345) | 0;
        const j = ((hash >>> 0) % (i + 1)) | 0;
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

export function CommentScoringContent() {
    const datastore = useDatastore();
    const instanceKey = useInstanceKey();
    const personaKey = usePersonaKey();

    // Import conversation data into this instance
    const { name, loading: importing, error: importError, reimport, importedAt } = useImportConversation();
    const conversationName = name || instanceKey;

    // Read the imported comments through the ranking-eval oRPC resource,
    // most-recent order (this tab rates comments individually — it doesn't
    // compare rankers, so any stable order works). The tree's roots are the
    // top-level comments the tool scores.
    const { tree, isPending: commentsPending } = useEvalConversationComments('most-recent');
    const topLevelComments = tree.map((node) => node.comment);

    // Stable shuffle per user — same user always sees same order, different
    // users see different orders
    const shuffledComments = seededShuffle(topLevelComments, personaKey || '');

    // Navigation state
    const selectedSet = useSelectedEvalConversationSet();
    const selectedKeys = selectedSet?.conversationKeys || [];
    const allConversations = useImportableEvalConversations();

    // Comment ratings for this conversation (fetched via admin API)
    const { ratings: conversationRatings, refresh: refreshRatings } = useEvalCommentRatings();

    const handleNavigate = (key: string) =>
        datastore.gotoInstanceScreen({
            structureKey: 'ranking-eval',
            instanceKey: 'one',
            screenKey: `comment-scoring/${key}`,
            replace: true,
        });

    if (importing || commentsPending) {
        return <LoadingSpinnerContainer label={t('ranking-eval.importing', 'Importing conversation...')} />;
    }

    if (importError) {
        return <UtilityText text={`Error: ${importError}`} type='small' />;
    }

    return (
        <VertBox gap={24}>
            <VertBox gap={8}>
                <Heading text={conversationName} type='large' />
                <UtilityText label={t('ranking-eval.comment_scoring_desc', 'Rate each comment on a 1-5 star scale')} type='small' />
            </VertBox>
            <SideBySideNavigationBar
                allConversations={allConversations}
                importedAt={importedAt}
                instanceKey={instanceKey}
                onNavigate={handleNavigate}
                onRefresh={reimport}
                refreshing={importing}
                selectedKeys={selectedKeys}
            />
            <StarRatingLegend />
            <Heading label={t('ranking-eval.comments_random_order', 'Comments in random order')} />
            {shuffledComments.length === 0 ? (
                <UtilityText label={t('ranking-eval.no_comments', 'This conversation has no comments.')} type='small' />
            ) : (
                <VertBox gap={0}>
                    {shuffledComments.map((comment, index) => (
                        <View key={comment.commentKey}>
                            {index > 0 && <Separator />}
                            <Pad size={16} />
                            <ScoredComment
                                comment={comment}
                                conversationRatings={conversationRatings}
                                onRatingSubmitted={refreshRatings}
                                personaKey={personaKey}
                            />
                            <Pad size={16} />
                        </View>
                    ))}
                </VertBox>
            )}
        </VertBox>
    );
}
